import errorToString from '../misc/errorToString';
import { z } from 'zod';

export interface AppErrorOptions {
    cause?: unknown;
    errorCode?: string;
    details?: string;
}

export class AppError extends Error {
    errorCode?: string;
    details?: string;

    constructor(message: string, { cause, errorCode, details }: AppErrorOptions = {}) {
        super(errorToString(cause ?? message), { cause });
        this.message = message;
        this.errorCode = errorCode;
        this.details = details;
    }
}

export class ApiError extends AppError {
    constructor(
        public statusCode: number,
        message: string,
        options?: AppErrorOptions,
    ) {
        super(message, options);
    }
}

export function badRequest(message = 'Bad Request', options?: Omit<AppErrorOptions, 'statusCode'>) {
    return new ApiError(400, message, options);
}

export function unauthenticated(
    message = 'Unauthenticated',
    options?: Omit<AppErrorOptions, 'statusCode'>,
) {
    return new ApiError(401, message, options);
}

export function unauthorized(
    message = 'Unauthorized',
    options?: Omit<AppErrorOptions, 'statusCode'>,
) {
    return new ApiError(403, message, options);
}

export function notFound(message = 'Not Found', options?: Omit<AppErrorOptions, 'statusCode'>) {
    return new ApiError(404, message, options);
}

export function serviceUnavailable(
    message = 'Service Unavailable',
    options?: Omit<AppErrorOptions, 'statusCode'>,
) {
    return new ApiError(503, message, options);
}

const ApiErrorSchema = z.object({
    message: z.string(),
    errorCode: z.string().optional(),
    details: z.string().optional(),
});

export class ServerError extends ApiError {
    static fromResponse(
        method: string,
        url: string,
        statusCode: number,
        response: string,
        cause?: unknown,
    ) {
        try {
            const parsed = JSON.parse(response);
            const { message, errorCode, details } = ApiErrorSchema.parse(parsed);

            return new ServerError(
                method,
                url,
                response,
                statusCode,
                `${method} ${url} failed with ${statusCode}: ${message}`,
                {
                    errorCode,
                    details,
                    cause,
                },
            );
        } catch {
            return new ServerError(
                method,
                url,
                response,
                statusCode,
                `${method} ${url} failed with ${statusCode}`,
                {
                    cause,
                },
            );
        }
    }

    constructor(
        public method: string,
        public url: string,
        public response: string,
        statusCode: number,
        message: string,
        options?: AppErrorOptions,
    ) {
        super(statusCode, message, options);
    }
}
